import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import authService from '../services/authService';
import tokenManager from '../utils/tokenManager';
import { fetchWsTicket } from '../utils/wsTicket';
import { setLiveVideo, openLiveModal } from "../features/liveSlice";
import { signOut } from '../features/authSlice';

// Simplified Live WebSocket Hook
function useLiveWebSocket() {
  const dispatch = useDispatch();
  const { status: configStatus } = useSelector((state) => state.channel);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const configId = useSelector((state) => state.channel.configId);
  const persona = useSelector((state) => state.channel.persona);

  const [status, setStatus] = useState('disconnected');
  const [forceReconnectTrigger, setForceReconnectTrigger] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !configId || configStatus !== 'loaded') {
      console.log('🔴 Live WS: Prerequisites not met, not connecting:', { 
        isAuthenticated, 
        configId: !!configId, 
        configStatus 
      });
      setStatus('disconnected');
      return;
    }

    console.log('🟢 Live WS: All prerequisites met, connecting...', {
      isAuthenticated,
      configId,
      configStatus
    });

    let ws;
    let reconnectTimer;
    let connectTimer;
    let keepaliveTimer;
    let reconnectAttempts = 0;
    let ticketRetried = false;
    let isCleaningUp = false;
    let lastActivity = Date.now();
    const maxReconnectAttempts = 5;
    const keepaliveInterval = 30000; // 30 seconds
    const keepaliveTimeout = 5000; // 5 second timeout for keepalive response

    async function connect() {
      if (reconnectAttempts >= maxReconnectAttempts) {
        console.log('Live WS: Max reconnect attempts reached, giving up');
        setStatus('failed');
        return;
      }

      try {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log(`Live WS: Attempting to get auth token... (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
        }
        const token = await authService.getToken();
        const cognitoSub = authService.getSub();
        if (!token) {
          console.warn('Live WS: No authentication token available');
          reconnectAttempts++;
          if (reconnectAttempts < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 5000);
            console.log(`Live WS: Retrying in ${delay}ms...`);
            reconnectTimer = setTimeout(connect, delay);
          } else {
            console.log('Live WS: Max attempts reached, stopping retries');
            setStatus('failed');
          }
          return;
        }
        
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('Live WS: Got auth token, fetching WS ticket...');
        }

        let ticket;
        try {
          ticket = await fetchWsTicket(token);
        } catch (err) {
          console.warn('Live WS: Failed to fetch WebSocket ticket:', err.message);
          setStatus('failed');
          return;
        }

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const apiBaseURL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://api.ask-cara.com/ws';
        const wsUrl = `${wsProtocol}//${apiBaseURL.replace(/wss?:\/\//,'').replace('/ws','')}/ws/live?ticket=${ticket}&config_id=${configId}&persona=${persona || ''}${cognitoSub ? `&follow_id=${cognitoSub}` : ''}`;

        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('Live WS: Connecting...');
        }

        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('Live WS connected');
          setStatus('connected');
          reconnectAttempts = 0;
          ticketRetried = false;
          lastActivity = Date.now();
          startKeepalive();
        };

        ws.onclose = (event) => {
          if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
            console.log('Live WS closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
          }
          
          clearTimeout(keepaliveTimer);
          setStatus('disconnected');
          
          // Ticket missing or expired — retry once with a fresh ticket
          if (event.code === 4001 || event.code === 4003) {
            if (!ticketRetried) {
              ticketRetried = true;
              console.warn('🔐 Live WS: Ticket rejected (code', event.code, '), retrying with fresh ticket');
              reconnectTimer = setTimeout(connect, 0);
            } else {
              console.warn('🔐 Live WS: Ticket retry failed, giving up');
              setStatus('failed');
            }
            return;
          }

          // Check if closure was due to authentication failure (code 4401 or 1008)
          if (event.code === 4401 || event.code === 1008 || (event.reason && event.reason.includes('auth'))) {
            console.warn('🔐 Live WS closed due to authentication failure');
            // Check if token is expired
            tokenManager.isTokenExpired().then(expired => {
              if (expired) {
                console.warn('🔐 Token expired, triggering sign out');
                dispatch(signOut());
              }
            });
            return;
          }
          
          // Don't reconnect if we're cleaning up or if it was a normal close
          if (isCleaningUp || event.code === 1000) {
            if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
              console.log('Live WS: Not reconnecting (cleanup or normal close)');
            }
            return;
          }
          
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
            console.log(`Live WS: Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
            reconnectTimer = setTimeout(connect, delay);
          } else {
            console.log('Live WS: Max reconnect attempts reached after close');
            setStatus('failed');
          }
        };

        ws.onerror = (error) => {
          console.error('Live WS error:', error);
        };

        ws.onmessage = (event) => {
          // Update lastActivity on ANY message received (including empty heartbeats)
          lastActivity = Date.now();
          
          try {
            // Check if message data is empty or invalid - these are backend heartbeats
            if (!event.data || event.data.trim() === '') {
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Live WS: Received empty heartbeat message');
              }
              return;
            }
            
            console.log('🔴 Live WS: Raw message received:', event.data.substring(0, 200));
            
            const msg = JSON.parse(event.data);
            
            console.log('🔴 Live WS: Parsed message:', msg);
            
            // Handle connection status messages
            if (msg.status === 'connected' || msg.status === 'received') {
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Live WS: Status message:', msg.message || msg.status);
              }
              return;
            }
            
            // Handle keepalive responses
            if (msg.type === 'pong') {
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Live WS: Keepalive pong received');
              }
              return;
            }
            
            // Handle ping requests from server
            if (msg.type === 'ping') {
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'pong' }));
              }
              return;
            }
            
            console.log('🔴 Live WS: Message type:', msg.type);
            
            if (msg.type === 'video' || msg.type === 'Live' || msg.type === 'image') {
              console.log('🔴 Live WS: Dispatching setLiveVideo with:', msg);
              // TEMPORARY: force all live websocket events to play unmuted,
              // regardless of the payload's unmuted flag. Remove this override to
              // restore per-event audio control from the backend payload.
              dispatch(setLiveVideo({ ...msg, unmuted: true }));
              dispatch(openLiveModal());
            } else if (msg.type) {
              console.warn('Unknown live message type:', msg.type);
            } else {
              // Message has no type field - might be a status or acknowledgment
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Live WS: Received message without type field:', msg);
              }
            }
          } catch (error) {
            console.error('Error parsing live message:', error);
            console.error('Received data:', event?.data ? `"${event.data.substring(0, 100)}"` : 'undefined');
          }
        };

      } catch (error) {
        console.error('Live WS connection error:', error);
        reconnectAttempts++;
        if (reconnectAttempts < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
          console.log(`Live WS: Reconnecting after error in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
          reconnectTimer = setTimeout(connect, delay);
        } else {
          console.log('Live WS: Max reconnect attempts reached after error');
          setStatus('failed');
        }
      }
    }

    function startKeepalive() {
      if (keepaliveTimer) {
        clearTimeout(keepaliveTimer);
      }
      
      keepaliveTimer = setTimeout(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          const timeSinceLastActivity = Date.now() - lastActivity;
          
          if (timeSinceLastActivity >= keepaliveInterval) {
            if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
              console.log('Live WS: Sending keepalive ping');
            }
            ws.send(JSON.stringify({ type: 'ping' }));
            
            // Set a timeout to detect if keepalive fails
            setTimeout(() => {
              const currentTimeSinceActivity = Date.now() - lastActivity;
              if (currentTimeSinceActivity > keepaliveInterval + keepaliveTimeout) {
                console.warn('Live WS: Keepalive timeout, forcing reconnect');
                if (ws) {
                  ws.close(1000, 'Keepalive timeout');
                }
              }
            }, keepaliveTimeout);
          }
          
          // Schedule next keepalive check
          startKeepalive();
        }
      }, Math.max(1000, keepaliveInterval - (Date.now() - lastActivity)));
    }

    // Debounce initial connection to prevent rapid reconnects when effect re-runs
    connectTimer = setTimeout(connect, 100);

    return () => {
      isCleaningUp = true;
      clearTimeout(reconnectTimer);
      clearTimeout(connectTimer);
      clearTimeout(keepaliveTimer);
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      }
    };
  }, [isAuthenticated, configId, persona, configStatus, forceReconnectTrigger, dispatch]);

  const forceReconnect = () => {
    setForceReconnectTrigger(prev => prev + 1);
  };

  return { status, forceReconnect };
}

export default useLiveWebSocket;
